import React, {useState, useCallback, useEffect} from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import FacebookIcon from '@material-ui/icons/Facebook';
// import AppleIcon from '@material-ui/icons/Apple';
import fetchJSON from '../utils/fetchJSON';
import getCSRFToken from '../utils/gaia/getCSRFToken';
import gettext from '../utils/django/gettext';
import {GRANITE_6, GRANITE_3, GRANITE_1} from '../colors';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import {theme} from '../styles';
import Typography from '@material-ui/core/Typography';
import qs from 'qs';
import useSyncPromise from '../utils/react/useSyncPromise';
import useUser from '../hooks/useUser';
import {setLocalStorageItem} from '../utils/localStorage';
import {onboardingLocalStorageKey} from './map2/popups/UserOnboarding';

interface Response {
    code: number;
}

// eslint-disable-next-line prefer-arrow-callback
export default React.forwardRef(function RegistrationForm(
    {
        nextPage,
        setForm,
        style,
        onSubmitDone,
        onUserAuthenticated,
        analyticsContext,
    }: AuthFormProps,
    ref: React.Ref<HTMLDivElement>
) {
    const [, updateUser] = useUser();
    const [state, setState] = useState({email: '', password: ''});

    const [responsePromise, setResponsePromise] = useState<
        Promise<Response> | undefined
    >();
    const [response, error, isResponseLoaded] = useSyncPromise(
        responsePromise,
        undefined
    );
    const isLoaded = response ? response.code !== 1000 : isResponseLoaded;

    const onLoginClick = useCallback(() => {
        if (setForm) {
            setForm('login');
        } else {
            window.location.href = `/login/?next=${nextPage}`;
        }
    }, [setForm, nextPage]);

    useEffect(() => {
        if ((response || error) && onSubmitDone) onSubmitDone();
    }, [error, onSubmitDone, response]);

    useEffect(() => {
        if (response?.code === 1000) {
            updateUser();
            // display onboarding to new user
            setLocalStorageItem(onboardingLocalStorageKey, true);
            if (onUserAuthenticated) {
                onUserAuthenticated();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [response]);

    function onSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (!isLoaded) return;

        const csrf = getCSRFToken();

        setResponsePromise(
            fetchJSON('/register/addUser/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    ...(csrf && {'X-CSRFToken': csrf}),
                    accept: 'application/json',
                },
                body: qs.stringify({
                    email: state.email,
                    password: state.password,
                    analytics_context: analyticsContext,
                }),
            })
        );
    }

    return (
        <div style={{width: 275, ...style}} ref={ref}>
            <div style={{marginBottom: theme.spacing(2)}}>
                <Typography
                    variant='h4'
                    style={{
                        fontWeight: 'bold',
                        color: GRANITE_1,
                        display: 'inline',
                    }}
                >
                    {gettext('Sign Up')}
                </Typography>
                <Typography
                    variant='subtitle1'
                    style={{
                        display: 'inline',
                        marginLeft: theme.spacing(1),
                        color: GRANITE_3,
                    }}
                >
                    {"It's free!"}
                </Typography>
            </div>

            <form autoComplete='off' autoCapitalize='off' onSubmit={onSubmit}>
                <TextField
                    id='registration-email'
                    style={{display: 'block', marginBottom: theme.spacing(2)}}
                    fullWidth
                    label={gettext('Email')}
                    type='email'
                    variant='outlined'
                    autoFocus
                    required
                    value={state.email}
                    onChange={(e) =>
                        setState({...state, email: e.target.value})
                    }
                    error={response?.code === 1030}
                    helperText={
                        response?.code === 1030 &&
                        gettext('Email address already in use')
                    }
                    InputLabelProps={{required: false}}
                />
                <TextField
                    id='registration-password'
                    style={{display: 'block', marginBottom: theme.spacing(2)}}
                    fullWidth
                    label={gettext('Password')}
                    type='password'
                    variant='outlined'
                    required
                    value={state.password}
                    inputProps={{minLength: 8}}
                    onChange={(e) =>
                        setState({...state, password: e.target.value})
                    }
                    InputLabelProps={{required: false}}
                />

                {error && (
                    <Typography
                        variant='body1'
                        color='secondary'
                        id='unknown-error'
                        style={{paddingBottom: theme.spacing(2)}}
                    >
                        An unknown error occurred. Please try again.
                    </Typography>
                )}

                <Button
                    type='submit'
                    fullWidth
                    variant='contained'
                    size='large'
                    color='primary'
                    disabled={!isLoaded}
                    style={{
                        fontSize: 14,
                        // Since the registration form doesn't have a "Forgot password link" this
                        // marginTop ensures that the spacing and height remains consistent between the
                        // login and registration forms
                        marginTop: error ? 0 : 19,
                    }}
                >
                    {!isLoaded ? (
                        <CircularProgress
                            thickness={7}
                            size={24}
                            disableShrink
                        />
                    ) : (
                        gettext('Sign Up')
                    )}
                </Button>

                <Link
                    onClick={onLoginClick}
                    style={{
                        marginTop: theme.spacing(2),
                        display: 'block',
                        width: '100%',
                        textAlign: 'center',
                        fontSize: 14,
                    }}
                >
                    {gettext('Log in with existing account')}
                </Link>
            </form>

            <div
                style={{
                    textAlign: 'center',
                    borderTop: '1px solid',
                    borderTopColor: GRANITE_6,
                    height: 1,
                    marginTop: theme.spacing(3),
                    marginBottom: theme.spacing(3),
                }}
            >
                <div
                    style={{
                        padding: '0 12px',
                        color: GRANITE_3,
                        backgroundColor: '#fff',
                        display: 'inline-block',
                        transform: 'translateY(-77%)',
                        fontSize: 12,
                    }}
                >
                    or
                </div>
            </div>

            <Button
                component='a'
                variant='contained'
                fullWidth
                size='large'
                style={{
                    backgroundColor: '#3b5998',
                    color: '#fff',
                    fontSize: 14,
                }}
                startIcon={<FacebookIcon />}
                href={`/login/facebook/?next=${nextPage}`}
                target='_blank'
                rel='noopener'
                disabled={!isLoaded}
            >
                {gettext('Sign up with Facebook')}
            </Button>

            {/* <Button
            component='a'
            variant='contained'
            fullWidth
            size='large'
            style={{
                backgroundColor: '#000000',
                color: '#FFFFFF',
                fontSize: 14,
                marginTop: theme.spacing(2),
            }}
            startIcon={<AppleIcon />}
            href={'https://appleid.apple.com/auth/authorize?client_id=com.gaiagps.www' +
            '&redirect_uri=https%3A%2F%2Fwww.gaiagps.com%2Fapple-signin%2F&' +
            'response_type=code id_token' +
            '&scope=email&response_mode=form_post'}
            disabled={!isLoaded}
            target='_blank'
            rel='noopener'
        >
            {gettext('Sign up with Apple')}
        </Button> */}
        </div>
    );
});
